import { useEffect, useState } from "react";
import api from "../lib/api";
import { useApp } from "../context/AppContext";
import { Users, CheckCircle2, Send } from "lucide-react";
import { toast } from "sonner";

export default function Refer() {
  const { customer } = useApp();
  const [referrals, setReferrals] = useState([]);
  const [form, setForm] = useState({
    referred_name: "", referred_mobile: "", referred_location: "",
    referral_date: new Date().toISOString().slice(0, 10), comments: "",
  });
  const [busy, setBusy] = useState(false);

  const load = () => {
    if (!customer) return;
    api.get(`/customers/${customer.id}/manual-referrals`).then(r => setReferrals(r.data));
  };
  useEffect(() => { load(); }, [customer]); // eslint-disable-line

  const submit = async (e) => {
    e.preventDefault();
    for (const k of ["referred_name", "referred_mobile", "referred_location", "referral_date"]) {
      if (!form[k]) { toast.error(`Please fill ${k.replace(/_/g, " ")}`); return; }
    }
    setBusy(true);
    try {
      await api.post(`/customers/${customer.id}/manual-referrals`, form);
      toast.success("Referral recorded");
      setForm({ referred_name: "", referred_mobile: "", referred_location: "", referral_date: new Date().toISOString().slice(0, 10), comments: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    } finally { setBusy(false); }
  };

  if (!customer) return <div className="p-20 text-center text-[#8A8A8A]">Please sign in.</div>;

  return (
    <div className="max-w-screen-2xl mx-auto px-6 md:px-12 py-16" data-testid="refer-page">
      <p className="label-eyebrow">Refer &amp; Share</p>
      <h1 className="font-editorial text-5xl sm:text-6xl tracking-editorial mt-3 mb-4">Refer a friend or partner</h1>
      <p className="text-[#5C5C5C] max-w-2xl mb-12">Fill in the referral details. Our team will follow up and keep you posted on the status.</p>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2">
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6" data-testid="refer-form">
            <div>
              <label className="label-eyebrow block mb-1">Date of Referral</label>
              <input type="date" className="input-editorial" value={form.referral_date} onChange={(e) => setForm({ ...form, referral_date: e.target.value })} data-testid="refer-date" />
            </div>
            <div>
              <label className="label-eyebrow block mb-1">Name of the Person</label>
              <input className="input-editorial" value={form.referred_name} onChange={(e) => setForm({ ...form, referred_name: e.target.value })} placeholder="Full name" data-testid="refer-name" />
            </div>
            <div>
              <label className="label-eyebrow block mb-1">Mobile No.</label>
              <input className="input-editorial" value={form.referred_mobile} onChange={(e) => setForm({ ...form, referred_mobile: e.target.value })} placeholder="+91 98xxxx xxxxx" data-testid="refer-mobile" />
            </div>
            <div>
              <label className="label-eyebrow block mb-1">Location</label>
              <input className="input-editorial" value={form.referred_location} onChange={(e) => setForm({ ...form, referred_location: e.target.value })} placeholder="City, State" data-testid="refer-location" />
            </div>
            <div className="md:col-span-2">
              <label className="label-eyebrow block mb-1">Comments <span className="text-[#8A8A8A] normal-case tracking-normal">(optional)</span></label>
              <textarea rows={3} className="w-full bg-transparent border-b border-[#d6d3ce] py-3 outline-none focus:border-[#1E2C5A] text-sm resize-none" placeholder="Anything we should know?" value={form.comments} onChange={(e) => setForm({ ...form, comments: e.target.value })} data-testid="refer-comments" />
            </div>
            <div className="md:col-span-2">
              <button type="submit" disabled={busy} className="btn-clay" data-testid="refer-submit">
                {busy ? "Saving..." : "Submit Referral"} <Send className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </div>
          </form>
        </div>

        <div className="space-y-4">
          <div className="border border-[#1A1A1A]/10 p-6" data-testid="stat-total">
            <Users className="w-5 h-5 text-[#1E2C5A]" strokeWidth={1.5} />
            <p className="label-eyebrow mt-4">People You've Referred</p>
            <p className="font-editorial text-5xl mt-2">{referrals.length}</p>
          </div>
        </div>
      </div>

      <section className="mt-16" data-testid="i-have-referred">
        <h3 className="font-editorial text-3xl mb-6">I Have Referred</h3>
        {referrals.length === 0 ? (
          <p className="text-[#8A8A8A] text-sm">No referrals recorded yet.</p>
        ) : (
          <div className="divide-y divide-[#1A1A1A]/10">
            {referrals.map(r => (
              <div key={r.id} className="py-4 grid grid-cols-12 gap-4 items-center" data-testid={`referral-row-${r.id}`}>
                <div className="col-span-4">
                  <p className="font-medium">{r.referred_name}</p>
                  <p className="text-xs text-[#8A8A8A]">{r.referred_mobile} · {r.referred_location}</p>
                </div>
                <div className="col-span-3 text-xs">
                  <span className="label-eyebrow">Referred on</span><br/>
                  <span className="text-[#1E2C5A] font-medium">{r.referral_date}</span>
                </div>
                <div className="col-span-5 text-xs text-[#5C5C5C] italic">{r.comments || "—"}</div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
